import { Component, OnDestroy, OnInit } from '@angular/core';
import Map from 'ol/Map';
import { fromLonLat, getPointResolution } from 'ol/proj';
import { TileService } from '../../lead-dashboard/selected-leads-view/selected-leads-map/tile.service';
import { MatButton } from '@angular/material/button';
import { Lead } from '../../lead.model';
import { GeolocationService } from '../../services/geolocation/geolocation.service';
import { Feature } from 'ol';
import { Circle, Point } from 'ol/geom';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import VectorSource from 'ol/source/Vector';
import { initMap } from '../../reusable-components/open-layers.utils';
import {
  DialogService,
  OpenInputDialogOptions,
} from '../../services/dialog/dialog.service';
import { LeadService } from '../../services/leads/lead.service';
import { Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Coordinate } from 'ol/coordinate';

@Component({
  selector: 'app-map',
  imports: [MatButton, TranslatePipe],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss',
})
export class MapComponent implements OnInit, OnDestroy {
  public map!: Map;
  private meSource = new VectorSource();
  private circleSource = new VectorSource();
  private leadFeatures = new VectorSource();
  radius = 0;
  totalNumberOfLeads = 0;

  leadServiceSubscription: Subscription | undefined;
  translateServiceSubscription: Subscription | undefined;

  constructor(
    private tileService: TileService,
    private geolocationService: GeolocationService,
    private dialogService: DialogService,
    private leadService: LeadService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    const initialized = initMap(
      [this.meSource, this.circleSource, this.leadFeatures],
      this.tileService,
      'allLeadsMap',
      this.translateService,
    );
    this.map = initialized.map;
    this.translateServiceSubscription = initialized.subscription;
    this.subscribeLeadService();
  }

  openInputDialog() {
    const options: OpenInputDialogOptions = {
      inputCheckerFn: (input: string) => /^\d+(\.\d+)?$/.test(input),
      afterCloseFn: (text: string) => this.fetchLeads(parseFloat(text)),
      inputLabel: 'components.map.fetch-leads.label',
      text: 'components.map.fetch-leads.text',
      errorMessage: 'components.map.fetch-leads.error',
      defaultInput: this.radius.toString(),
      type: 'number',
    };

    this.dialogService.openInputDialog(options);
  }

  drawCircle(position: Coordinate) {
    const resolutionAtPoint = getPointResolution(
      this.map.getView().getProjection(),
      1,
      position,
    );
    const trueRadius =
      (this.leadService.getRadiusInKilometers() * 1000) / resolutionAtPoint;
    const circleFeature = new Feature({
      geometry: new Circle(position, trueRadius),
    });

    circleFeature.setStyle(
      new Style({
        fill: new Fill({ color: 'rgba(255,0,0,0.2)' }),
        stroke: new Stroke({ color: 'red', width: 2 }),
      }),
    );
    this.circleSource.clear();
    this.circleSource.addFeature(circleFeature);
  }

  animateToCircle(position: Coordinate) {
    const currentView = this.map.getView();
    const mapSize = this.map.getSize()!;
    const desiredPixelRadius = Math.min(...mapSize) / 4;
    const resolution = (this.radius * 1000) / desiredPixelRadius;
    const targetZoom = currentView.getZoomForResolution(resolution);

    currentView.animate({
      center: position,
      zoom: targetZoom,
      duration: 500,
    });
  }

  drawMe(position: Coordinate) {
    const meFeature = new Feature({ geometry: new Point(position) });

    const meImg = document.createElement('img');
    meImg.src = '../assets/material_icon_my_location.png';
    meImg.onload = () => {
      const meStyle = new Style({
        image: new Icon({
          anchor: [0.5, 0.5],
          img: meImg,
          scale: 0.4,
        }),
      });
      meFeature.setStyle(meStyle);
      this.meSource.clear();
      this.meSource.addFeature(meFeature);
    };
  }

  drawLeads() {
    const leads = this.leadService.getLeads();
    this.totalNumberOfLeads = leads.length;
    const leadImg = document.createElement('img');
    leadImg.src = '../assets/material_icon_location_filled.png';
    leadImg.onload = () => {
      const leadStyle = new Style({
        image: new Icon({
          anchor: [0.5, 0.5],
          img: leadImg,
          scale: 0.4,
        }),
      });
      this.leadFeatures.clear();
      leads.forEach((lead: Lead) => {
        const leadFeature = new Feature({
          geometry: new Point(
            fromLonLat([lead.coordinates.longitude, lead.coordinates.latitude]),
          ),
        });
        leadFeature.setStyle(leadStyle);
        this.leadFeatures.addFeature(leadFeature);
      });
    };
  }

  subscribeLeadService(): void {
    this.leadServiceSubscription = this.leadService.allLeadsChanges$.subscribe(
      () => {
        this.radius = this.leadService.getRadiusInKilometers();
        const coordinates = this.leadService.getCoordinates();
        const position = fromLonLat([
          coordinates.longitude,
          coordinates.latitude,
        ]);

        this.drawMe(position);
        this.drawCircle(position);
        this.animateToCircle(position);
        this.drawLeads();
      },
    );
  }

  async fetchLeads(radiusInKilometers: number) {
    const location = await this.geolocationService.getPosition();
    this.leadService.fetchLeads(
      location.coords.latitude,
      location.coords.longitude,
      radiusInKilometers,
    );
  }

  ngOnDestroy(): void {
    this.leadServiceSubscription?.unsubscribe();
    this.translateServiceSubscription?.unsubscribe();
  }
}
